import React, { useState, useEffect } from 'react';
import { Github, GitCommit, Star, GitFork, ExternalLink, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const GitHubActivity = () => {
  const [activities, setActivities] = useState([]);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { theme } = useTheme();

  const usernames = ['imkanchwala', 'mkanchwala'];

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        setLoading(true);
        
        // Fetch events from both profiles
        const eventsPromises = usernames.map(username =>
          fetch(`https://api.github.com/users/${username}/events/public?per_page=10`)
            .then(res => res.ok ? res.json() : [])
            .catch(() => [])
        );

        // Fetch repos from both profiles
        const reposPromises = usernames.map(username =>
          fetch(`https://api.github.com/users/${username}/repos?sort=updated&per_page=5`)
            .then(res => res.ok ? res.json() : [])
            .catch(() => [])
        );

        const [events1, events2] = await Promise.all(eventsPromises);
        const [repos1, repos2] = await Promise.all(reposPromises);

        // Combine and sort events by date
        const allEvents = [...events1, ...events2]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 8);

        // Combine and sort repos by update date
        const allRepos = [...repos1, ...repos2]
          .filter(repo => !repo.fork)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 6);

        setActivities(allEvents);
        setRepos(allRepos);
        setError(null);
      } catch (err) {
        console.error('Error fetching GitHub data:', err);
        setError('Unable to load GitHub activity');
      } finally {
        setLoading(false);
      }
    };

    fetchGitHubData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchGitHubData, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEventIcon = (type) => {
    switch (type) {
      case 'PushEvent':
        return <GitCommit className="h-4 w-4" />;
      case 'WatchEvent':
        return <Star className="h-4 w-4" />;
      case 'ForkEvent':
        return <GitFork className="h-4 w-4" />;
      default:
        return <Github className="h-4 w-4" />;
    }
  };

  const getEventDescription = (event) => {
    const repoName = event.repo?.name?.split('/')[1] || event.repo?.name;
    switch (event.type) {
      case 'PushEvent':
        const commits = event.payload?.commits?.length || 0;
        return `Pushed ${commits} commit${commits !== 1 ? 's' : ''} to ${repoName}`;
      case 'WatchEvent':
        return `Starred ${repoName}`;
      case 'ForkEvent':
        return `Forked ${repoName}`;
      case 'CreateEvent':
        return `Created ${event.payload?.ref_type || 'repository'} in ${repoName}`;
      case 'PullRequestEvent':
        return `${event.payload?.action} PR in ${repoName}`;
      case 'IssuesEvent':
        return `${event.payload?.action} issue in ${repoName}`;
      default:
        return `Activity in ${repoName}`;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <section id="github" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#1B3139] to-[#0f1f26]' 
        : 'bg-gradient-to-br from-white to-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Github className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Open Source
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            GitHub Activity
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Live updates from my GitHub contributions
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-[#FF3621] animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className={theme === 'dark' ? 'text-white/60' : 'text-gray-500'}>{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Recent Activity */}
            <div className={`border rounded-lg overflow-hidden shadow-xl ${
              theme === 'dark' 
                ? 'bg-[#1B3139] border-white/10' 
                : 'bg-white border-gray-200'
            }`}>
              <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${
                theme === 'dark' 
                  ? 'bg-white/5 border-white/10' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <h3 
                  className={`text-lg font-bold flex items-center space-x-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  <GitCommit className="h-5 w-5 text-[#FF3621]" />
                  <span>Recent Activity</span>
                </h3>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {activities.length === 0 ? (
                  <p className={`text-center py-4 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>No recent activity</p>
                ) : (
                  activities.map((event, index) => (
                    <div 
                      key={`${event.id}-${index}`}
                      className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                        theme === 'dark' 
                          ? 'bg-white/5 hover:bg-white/10' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="p-2 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded-lg text-[#FF3621]">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p 
                          className={`text-sm truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}
                          style={{ fontFamily: 'DM Sans, sans-serif' }}
                        >
                          {getEventDescription(event)}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span 
                            className={`text-xs font-mono ${
                              theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                            }`}
                            style={{ fontFamily: 'DM Mono, monospace' }}
                          >
                            @{event.actor?.login}
                          </span>
                          <span className={theme === 'dark' ? 'text-white/20' : 'text-gray-300'}>•</span>
                          <span 
                            className={`text-xs ${
                              theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                            }`}
                            style={{ fontFamily: 'DM Mono, monospace' }}
                          >
                            {formatDate(event.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Popular Repositories */}
            <div className={`border rounded-lg overflow-hidden shadow-xl ${
              theme === 'dark' 
                ? 'bg-[#1B3139] border-white/10' 
                : 'bg-white border-gray-200'
            }`}>
              <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${
                theme === 'dark' 
                  ? 'bg-white/5 border-white/10' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <h3 
                  className={`text-lg font-bold flex items-center space-x-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  <Star className="h-5 w-5 text-[#FF3621]" />
                  <span>Repositories</span>
                </h3>
              </div>
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {repos.length === 0 ? (
                  <p className={`text-center py-4 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>No repositories found</p>
                ) : (
                  repos.map((repo, index) => (
                    <a
                      key={`${repo.id}-${index}`}
                      href={repo.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block p-3 rounded-lg transition-colors group ${
                        theme === 'dark' 
                          ? 'bg-white/5 hover:bg-white/10' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p 
                            className={`text-sm font-medium group-hover:text-[#FF3621] transition-colors truncate ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                          >
                            {repo.name}
                          </p>
                          {repo.description && (
                            <p 
                              className={`text-xs mt-1 line-clamp-2 ${
                                theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                              }`}
                              style={{ fontFamily: 'DM Sans, sans-serif' }}
                            >
                              {repo.description}
                            </p>
                          )}
                        </div>
                        <ExternalLink className={`h-4 w-4 group-hover:text-[#FF3621] flex-shrink-0 ml-2 ${
                          theme === 'dark' ? 'text-white/40' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        {repo.language && (
                          <span 
                            className={`text-xs flex items-center space-x-1 ${
                              theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                            }`}
                            style={{ fontFamily: 'DM Mono, monospace' }}
                          >
                            <span className="w-2 h-2 rounded-full bg-[#FF3621]"></span>
                            <span>{repo.language}</span>
                          </span>
                        )}
                        <span 
                          className={`text-xs flex items-center space-x-1 ${
                            theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                          }`}
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          <Star className="h-3 w-3" />
                          <span>{repo.stargazers_count}</span>
                        </span>
                        <span 
                          className={`text-xs flex items-center space-x-1 ${
                            theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                          }`}
                          style={{ fontFamily: 'DM Mono, monospace' }}
                        >
                          <GitFork className="h-3 w-3" />
                          <span>{repo.forks_count}</span>
                        </span>
                      </div>
                    </a>
                  ))
                )}
              </div>
              <div className={`px-4 sm:px-6 py-3 border-t ${
                theme === 'dark' ? 'border-white/10' : 'border-gray-200'
              }`}>
                <div className="flex justify-center space-x-4">
                  {usernames.map(username => (
                    <a
                      key={username}
                      href={`https://github.com/${username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#FF3621] hover:text-[#FF5841] transition-colors flex items-center space-x-1"
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      <Github className="h-3 w-3" />
                      <span>@{username}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GitHubActivity;
